import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { API_URL } from '../../constants';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CloseIcon from '@material-ui/icons/Close';
import { Button } from '../../components/library';
import HoverableImageThumbnail from '../catalog/productImages/HoverableImageThumbnail';
import RefurbishedItemRelocator from '../inventory/relocate/ReburbishedItemRelocator';
import { QcBikeItem } from './BikeQcList';

export const BIKE_QC_DETAIL_ROUTE = '/fulfillment/bike-qc/:lineItemStatusId';

// Best-effort pre-fill: replace entry.XXXX keys with real IDs once confirmed from the form.
function buildFormUrl(baseUrl: string, sku: string | null, orderName: string): string {
  const params = new URLSearchParams();
  if (sku) params.set('entry.sku', sku);
  params.set('entry.order', orderName);
  const qs = params.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
}

type ActionState =
  | 'idle'
  | 'form_open'
  | 'fail_form_open'
  | 'passing'
  | 'failing'
  | 'passed'
  | 'failed'
  | 'done';

const BikeQcDetail: React.FC = () => {
  const { lineItemStatusId } = useParams<{ lineItemStatusId: string }>();
  const history = useHistory();

  const [bike, setBike] = useState<QcBikeItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<ActionState>('idle');
  const [resultBinCode, setResultBinCode] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [qcFormUrl, setQcFormUrl] = useState('');
  const [relocatorOpen, setRelocatorOpen] = useState(false);

  useEffect(() => {
    const fetchBike = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const response = await axios.get(`${API_URL}/fulfillment/qc/bikes`);
        const { items, qcAdjustmentsFormUrl } = (response as any).data;
        setQcFormUrl(qcAdjustmentsFormUrl ?? '');
        const found = (items as QcBikeItem[]).find(b => b.id === Number(lineItemStatusId));
        if (!found) {
          setFetchError('This item is no longer in the QC queue.');
        } else {
          setBike(found);
        }
      } catch {
        setFetchError('Failed to load QC item.');
      } finally {
        setLoading(false);
      }
    };
    fetchBike();
  }, [lineItemStatusId]);

  const handlePass = async (notes?: string) => {
    if (!bike) return;
    setActionState('passing');
    try {
      const res = await axios.post(`${API_URL}/fulfillment/qc/bikes/${bike.id}/pass`, {
        notes,
      });
      setResultBinCode((res as any).data?.fulfillmentBinCode ?? 'PICKED-BUFFER');
      setActionState('passed');
    } catch {
      setApiError('Failed to record QC pass. Please try again.');
      setActionState('idle');
    }
  };

  const handleFail = async () => {
    if (!bike) return;
    setActionState('failing');
    try {
      await axios.post(`${API_URL}/fulfillment/qc/bikes/${bike.id}/fail`, {});
      setActionState('failed');
      setRelocatorOpen(true);
    } catch {
      setApiError('Failed to record QC fail. Please try again.');
      setActionState('fail_form_open');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError || !bike) {
    return (
      <Box sx={{ p: 4 }}>
        <Button
          ordinality="secondary"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/fulfillment/bike-qc')}
        >
          Bike QC
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {fetchError ?? 'Item not found.'}
        </Alert>
      </Box>
    );
  }

  // ── Post-action screens ──────────────────────────────────────────────────────

  if (actionState === 'passed') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
          p: 4,
        }}
      >
        <CheckCircleOutlineIcon style={{ fontSize: 64, color: '#009320' }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>QC Passed</Typography>
        <Typography sx={{ fontSize: 16, color: 'grey.600' }}>Move bike to:</Typography>
        <Typography
          sx={{
            fontSize: 32,
            fontWeight: 700,
            letterSpacing: '2px',
            color: 'grey.900',
            border: '2px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            px: 4,
            py: 1.5,
          }}
        >
          {resultBinCode}
        </Typography>
        <Button
          ordinality="primary"
          onClick={() => history.push('/fulfillment/bike-qc')}
          style={{ marginTop: 16 }}
        >
          Back to Bike QC
        </Button>
      </Box>
    );
  }

  if (actionState === 'done') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
          p: 4,
        }}
      >
        <CheckCircleOutlineIcon style={{ fontSize: 64, color: '#009320' }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>QC Complete</Typography>
        <Typography sx={{ fontSize: 16, color: 'grey.600' }}>
          Bike has been flagged for work and relocated.
        </Typography>
        <Button
          ordinality="primary"
          onClick={() => history.push('/fulfillment/bike-qc')}
          style={{ marginTop: 16 }}
        >
          Back to Bike QC
        </Button>
      </Box>
    );
  }

  if (actionState === 'failed') {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2,
            p: 4,
          }}
        >
          <ErrorOutlineIcon style={{ fontSize: 64, color: '#d32f2f' }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>QC Failed — Work Needed</Typography>
          <Button
            ordinality="primary"
            onClick={() => setRelocatorOpen(true)}
            style={{ marginTop: 8 }}
          >
            Relocate Bike
          </Button>
          <Button
            ordinality="secondary"
            variant="text"
            onClick={() => history.push('/fulfillment/bike-qc')}
          >
            Back to Bike QC
          </Button>
        </Box>

        <Dialog
          open={relocatorOpen}
          onClose={() => setRelocatorOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            Relocate {bike.sku ?? 'Bike'}
            <IconButton
              size="small"
              onClick={() => setRelocatorOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {bike.sku && (
              <RefurbishedItemRelocator
                sku={bike.sku}
                onSuccess={() => {
                  setRelocatorOpen(false);
                  setActionState('done');
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ── Main detail view ─────────────────────────────────────────────────────────

  const isActing = actionState === 'passing' || actionState === 'failing';

  return (
    <Box sx={{ p: '2rem 4rem', maxWidth: 640 }}>
      {/* Back nav */}
      <Button
        ordinality="secondary"
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/fulfillment/bike-qc')}
      >
        Bike QC
      </Button>

      {/* Bike card */}
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          gap: 3,
          alignItems: 'flex-start',
          pb: 3,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box sx={{ width: 120, flexShrink: 0 }}>
          <HoverableImageThumbnail
            src={bike.imageUrl ?? undefined}
            identifier={bike.sku ?? undefined}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '0.5px' }}>
            {bike.sku ?? '—'}
          </Typography>
          {bike.title && (
            <Typography sx={{ fontSize: 15, color: 'grey.700' }}>{bike.title}</Typography>
          )}
          <Typography sx={{ fontSize: 13, color: 'grey.500', mt: 0.5 }}>
            Order:{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: 'grey.700',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => history.push(`/fulfillment/orders/${bike.shopifyOrderId}`)}
            >
              {bike.shopifyOrderName}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'grey.500' }}>
            Location:{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'grey.700' }}>
              {bike.qcBinCode ?? 'QC-BUFFER'}
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: 'grey.500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          QC Decision
        </Typography>

        {/* Pass */}
        {actionState === 'idle' && (
          <Button
            ordinality="primary"
            disabled={isActing}
            onClick={() => handlePass()}
            style={{ width: 200 }}
          >
            Pass
          </Button>
        )}

        {/* Pass with Adjustments */}
        {actionState === 'idle' && (
          <Button
            ordinality="primary"
            variant="outlined"
            disabled={isActing}
            onClick={() => {
              window.open(
                buildFormUrl(qcFormUrl, bike.sku, bike.shopifyOrderName),
                '_blank',
                'noopener',
              );
              setActionState('form_open');
            }}
            style={{ width: 200 }}
          >
            Pass + Report QC Issue
          </Button>
        )}

        {/* Pass + Report QC Issue — confirm after form submitted in new tab */}
        {actionState === 'form_open' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              maxWidth: 600,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <OpenInNewIcon style={{ fontSize: 16, color: '#757575' }} />
              <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                QC form opened in a new tab. Submit it, then confirm below.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                ordinality="primary"
                size="small"
                onClick={() => handlePass('Pass + Report QC Issue via form')}
              >
                Done — Move to Buffer
              </Button>
              <Button
                ordinality="secondary"
                size="small"
                onClick={() => {
                  window.open(
                    buildFormUrl(qcFormUrl, bike.sku, bike.shopifyOrderName),
                    '_blank',
                    'noopener',
                  );
                }}
              >
                Re-open Form
              </Button>
              <Button ordinality="secondary" size="small" onClick={() => setActionState('idle')}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* Fail — opens external form in new tab */}
        {actionState === 'idle' && (
          <Button
            ordinality="warning"
            variant="outlined"
            disabled={isActing}
            onClick={() => {
              window.open(
                buildFormUrl(qcFormUrl, bike.sku, bike.shopifyOrderName),
                '_blank',
                'noopener',
              );
              setActionState('fail_form_open');
            }}
            style={{ width: 200, color: '#d32f2f', borderColor: '#d32f2f' }}
          >
            Fail (Work Needed)
          </Button>
        )}

        {/* Fail — confirm after form submitted in new tab */}
        {actionState === 'fail_form_open' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              border: '1px solid',
              borderColor: 'error.light',
              borderRadius: 2,
              maxWidth: 600,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <OpenInNewIcon style={{ fontSize: 16, color: '#757575' }} />
              <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                QC fail form opened in a new tab. Submit it, then confirm below.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                ordinality="primary"
                size="small"
                onClick={handleFail}
                style={{ background: '#d32f2f', borderColor: '#d32f2f' }}
              >
                Done — Confirm Fail &amp; Relocate
              </Button>
              <Button
                ordinality="secondary"
                size="small"
                onClick={() => {
                  window.open(
                    buildFormUrl(qcFormUrl, bike.sku, bike.shopifyOrderName),
                    '_blank',
                    'noopener',
                  );
                }}
              >
                Re-open Form
              </Button>
              <Button ordinality="secondary" size="small" onClick={() => setActionState('idle')}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {isActing && <CircularProgress size={24} />}
      </Box>

      {/* Error snackbar */}
      <Snackbar
        open={!!apiError}
        autoHideDuration={5000}
        onClose={() => setApiError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setApiError(null)}>
          {apiError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BikeQcDetail;
